/**
 * ContractorLogoEditor — F11 T11.8.1.
 *
 * Mały panel w nagłówku karty kontrahenta: miniaturka logo (lub placeholder)
 * + button „Zmień" (file picker) + button „Usuń" (gdy ustawione).
 *
 * Backend: POST/DELETE /api/contractors/:id/logo (multipart `file`); obrazek pobierany z GET tej
 * trasy z tokenem (`logoUrl` = ścieżka API z karty kontrahenta, nie bezpośredni link do magazynu).
 * Walidacja po stronie klienta: tylko JPEG/PNG/WebP, max 2MB.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Image as ImageIcon, Trash2, Upload } from 'lucide-react';

import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { runConflictText } from '@/lib/record-procedures/run-texts';
import { extractRunConflict } from '@/lib/record-procedures/runs-api';
import { useAuthedImage } from '@/lib/use-authed-image';

const ALLOWED = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_BYTES = 2 * 1024 * 1024;

interface Props {
  contractorId: string;
  logoUrl: string | null;
}

/** Edytor logo nie zna napisów przycisków SQL z układu — komunikat blokady użyje nazwy procedury. */
const NO_BUTTON_LABELS: ReadonlyMap<string, string> = new Map();

export function ContractorLogoEditor({ contractorId, logoUrl }: Props) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const logoSrc = useAuthedImage(logoUrl);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['contractor', contractorId] });
  }

  function getErrorCode(err: unknown): string {
    return isAxiosError(err)
      ? ((err.response?.data as { error?: string })?.error ?? 'GENERIC')
      : 'GENERIC';
  }

  function showError(err: unknown) {
    // Odrzucone na czas zadania przycisku SQL na tym rekordzie — które zadanie blokuje.
    const runConflict = extractRunConflict(err);
    if (runConflict?.code === 'RECORD_LOCKED_BY_RUN') {
      toast.show({
        variant: 'error',
        title: runConflictText(runConflict, NO_BUTTON_LABELS, t, i18n.language),
      });
      return;
    }
    toast.show({
      variant: 'error',
      title: t(`tenant.contractors.logo.error.${getErrorCode(err)}`, {
        defaultValue: t('tenant.contractors.logo.error.GENERIC'),
      }),
    });
  }

  const uploadMutation = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData();
      fd.append('file', f);
      await api.post(`/contractors/${contractorId}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.logo.toast.uploaded'),
      });
      refresh();
    },
    onError: showError,
    onSettled: () => setBusy(false),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/contractors/${contractorId}/logo`);
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.logo.toast.removed'),
      });
      refresh();
    },
    onError: showError,
    onSettled: () => setBusy(false),
  });

  function handlePick(file: File | null) {
    if (!file) return;
    if (!ALLOWED.has(file.type)) {
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.logo.error.UNSUPPORTED_MEDIA_TYPE'),
      });
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.logo.error.FILE_TOO_LARGE'),
      });
      return;
    }
    setBusy(true);
    uploadMutation.mutate(file);
  }

  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-border-secondary bg-bg-elevated p-2"
      data-testid="contractor-logo-editor"
    >
      <div className="h-16 w-16 flex-none overflow-hidden rounded bg-bg-secondary">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={t('tenant.contractors.logo.alt')}
            className="h-full w-full object-cover"
            data-testid="contractor-logo-image"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center text-text-tertiary"
            data-testid="contractor-logo-placeholder"
          >
            <ImageIcon size={28} aria-hidden="true" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(ev) => handlePick(ev.target.files?.[0] ?? null)}
          // Logo wgrywa się od razu po wyborze pliku — to nie jest „niezapisana zmiana" formularza,
          // w którym edytor jest osadzony (zabezpieczenie zamknięcia okna).
          data-no-dirty
          data-testid="contractor-logo-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded border border-border-secondary px-2 py-1 text-xs text-text-primary hover:bg-bg-secondary disabled:opacity-50"
          data-testid="contractor-logo-upload-btn"
        >
          <Upload size={12} aria-hidden="true" />
          {logoUrl
            ? t('tenant.contractors.logo.replaceBtn')
            : t('tenant.contractors.logo.uploadBtn')}
        </button>
        {logoUrl && (
          <button
            type="button"
            onClick={() => {
              setBusy(true);
              deleteMutation.mutate();
            }}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/20"
            data-testid="contractor-logo-remove-btn"
          >
            <Trash2 size={12} aria-hidden="true" />
            {t('tenant.contractors.logo.removeBtn')}
          </button>
        )}
      </div>
    </div>
  );
}
